import {
  Component,
  EventEmitter,
  forwardRef,
  Host,
  Input,
  Output,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { TreeNode } from 'primeng/api';
import { DropdownChangeEvent } from 'primeng/dropdown';
import { TreeFilterEvent } from 'primeng/tree';
import { STATUS } from '../../common/use-loading.model';
import { EoBaseFormControlComponent } from '../eo-base-form-control/eo-base-form-control.component';
import { EoTreeDropdownConfig } from './eo-tree-dropdown.model';
import { EoTreeDropdownService } from './eo-tree-dropdown.service';

@Component({
  selector: 'eo-tree-dropdown',
  templateUrl: './eo-tree-dropdown.component.html',
  styleUrls: ['./eo-tree-dropdown.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EoTreeDropdownComponent),
      multi: true,
    },
    EoTreeDropdownService,
  ],
})
export class EoTreeDropdownComponent<T> extends EoBaseFormControlComponent {
  protected override _config?: EoTreeDropdownConfig<T>;

  override get config() {
    return this._config;
  }
  override set config(val: EoTreeDropdownConfig<T> | undefined) {
    this._config = val;
  }

  httpData$ = this.dataService?.data$;
  httpStatus$ = this.dataService?.status$;
  httpError$ = this.dataService?.error$;

  @Input() dropdownIcon?: string;
  @Input() inputStyleClass?: string;
  @Input() optionLabel?: string;
  @Input() optionValue?: string;
  @Input() options?: TreeNode<T>[];
  @Input() dropdownStyleClass?: string;
  @Input() placeholder?: string;
  @Input() loadingPlaceholder?: string;
  @Input() showClear: boolean | null = null;
  @Input() filter?: boolean;
  @Input() group = false;
  @Input() filterBy?: string;
  @Input() filterFields?: any[];
  @Input() filterPlaceholder?: string;
  @Input() appendTo?: any;
  @Input() virtualScrollSize?: number;
  @Input() virtualScroll: boolean = false;
  @Input() override loading = false;
  @Input() apiUrl?: string;

  @Output() onFilter: EventEmitter<TreeFilterEvent> = new EventEmitter();
  @Output() override onChange: EventEmitter<DropdownChangeEvent> =
    new EventEmitter();

  constructor(@Host() private readonly dataService?: EoTreeDropdownService<T>) {
    super();
  }

  fetchData(): void {
    const apiUrl = this.getApiUrl();
    if (!apiUrl) return;

    this.dataService?.getData(apiUrl).subscribe();
  }

  override ngOnInit(): void {
    this.fetchData();
  }

  protected override onChangeHandler(event: DropdownChangeEvent) {
    super.onChangeHandler(event);
  }

  protected hasInputIcon(): boolean {
    return this.dropdownIcon != null || this.config?.inputIcon != null;
  }

  protected getDropdownIcon(): string {
    if (this.loading) return 'pi pi-spinner pi-spin';
    return this.dropdownIcon ?? this.config?.inputIcon ?? 'pi pi-chevron-down';
  }

  protected getPlaceholder(): string {
    if (this.getLoading())
      return this.loadingPlaceholder ?? this.config?.loadingPlaceholder ?? '';

    return this.placeholder ?? this.config?.placeholder ?? '';
  }

  protected getInputStyleClass(): string {
    return this.inputStyleClass ?? this.config?.inputStyleClass ?? '';
  }

  protected getOptionLabel(): string {
    return this.optionLabel ?? this.config?.optionLabel ?? '';
  }

  protected getOptionValue(): string {
    return this.optionValue ?? this.config?.optionValue ?? '';
  }

  protected getOptions(): TreeNode<T>[] {
    return (
      this.options ?? this.config?.options ?? this.httpData$?.getValue() ?? []
    );
  }

  protected getDropdownStyleClass(): string {
    return this.dropdownStyleClass ?? this.config?.dropdownStyleClass ?? '';
  }

  protected getLoadingPlaceholder(): string {
    return this.loadingPlaceholder ?? this.config?.loadingPlaceholder ?? '';
  }

  protected getLoading(): boolean {
    return this.loading || this.httpStatus$?.getValue() === STATUS.loading;
  }

  protected getShowClear(): boolean {
    return this.showClear ?? this.config?.showClear ?? false;
  }

  protected hasFilter(): boolean {
    if (this.filter !== undefined) return this.filter;

    return this.config?.filter ?? false;
  }

  protected getFilterBy(): string {
    return this.filterBy ?? this.config?.filterBy ?? '';
  }

  protected getFilterFields(): any[] {
    return this.filterFields ?? this.config?.filterFields ?? [];
  }

  protected getFilterPlaceholder(): string {
    return this.filterPlaceholder ?? this.config?.filterPlaceholder ?? '';
  }

  protected getAppendTo(): any {
    return this.appendTo ?? this.config?.appendTo ?? '';
  }
  protected getGroup(): boolean {
    return this.group ?? this.config?.group ?? false;
  }

  protected getVirtualScrollSize(): number {
    return this.virtualScrollSize ?? this.config?.virtualScrollSize ?? 0;
  }

  protected getVirtualScroll(): boolean {
    return this.virtualScroll ?? this.config?.virtualScroll ?? false;
  }

  protected getApiUrl(): string | undefined {
    return this.apiUrl ?? this.config?.apiUrl;
  }
}
